import React, {useEffect, useMemo} from 'react';
import {createSearchParams, useSearchParams} from "react-router-dom";
import BackendForm from "../components/forms/BackendForm";
import Pages from "../components/layout/Pages";
import {routes} from "../utils";
import useAuth from "../components/hooks/useAuth";
import {useNavigate} from "react-router";
import {At, Lock} from "react-bootstrap-icons";
import {backendEndpoints} from "../libs/backendEndpoints";
import FormFiller from "../components/forms/FormFiller";
import {User as BackendUser} from "fearless-common/src/backendModels";
import {Alert, Button, useTitle} from 'fearless-common-ui';
import {FloatingLabelField} from "../components/forms";

const formId = 'login-form';

const answerSets = [
  {
    title: 'A. Williams',
    answers: [{name: 'email_or_username', value: 'alex.williams@divvly.com'}, {name: 'password', value: 'password'}]
  },
  {
    title: 'S. Hariri',
    answers: [{name: 'email_or_username', value: 'sam.hariri@divvly.com'}, {name: 'password', value: 'password'}]
  },
];

/**
 * Public login page. Wraps {@link LoginPage.Form} with the page chrome (title,
 * heading, "Create an account" call-to-action).
 *
 * Query-string driven behavior:
 *   - `redirect` — forwarded to the sign-up page on the "Create an account"
 *     link, so a visitor who didn't have an account can still land on the same
 *     destination after signing up. The form itself uses the same `redirect`
 *     param to determine where to send the user after a successful login (see
 *     {@link LoginPage.Form}).
 *
 * The redirect value is re-encoded via `createSearchParams` so a redirect
 * target that contains its own query string (e.g. `/photo51?foo=bar`) doesn't
 * collide with the sign-up page's own params.
 */
export default function LoginPage() {
  useTitle('Log in');
  const [sp] = useSearchParams();
  const redirect = sp.get('redirect');
  const signUpHref = redirect
    ? `${routes.signUp}?${createSearchParams({redirect}).toString()}`
    : routes.signUp;

  return (
    <Pages.Centred width="xs">
      <h1 className="text-center mb-5">Log in</h1>
      <LoginPage.Form />
      <div className="bg-dark-transparent py-5 px-5 text-center mt-5 rounded-bottom-lg">
        <strong className="text-lg d-block mb-3 text-white">Don't have an account yet?</strong>
        <a href={signUpHref}>
          <Button size="lg">Create an account</Button>
        </a>
      </div>
    </Pages.Centred>
  );
}

/**
 * Reusable login form. Mounted standalone by {@link LoginPage} and embedded by
 * other flows (e.g. the group invitation variant of SignUp) that need an
 * inline login option without the surrounding page chrome.
 *
 * Post-login redirect precedence:
 *   1. `redirectUrl` prop — caller-supplied destination (wins over everything).
 *   2. `?redirect` query param on the current URL.
 *   3. {@link routes.landing} (default).
 *
 * If the backend response sets `two_factor: true`, the user is sent to the
 * 2FA challenge route regardless of the props above.
 *
 * Side effects on mount:
 *   - When the user is already authenticated and no `?message` is showing, the
 *     form auto-navigates to the dashboard. The `message` carve-out exists so
 *     transient banners (e.g. "session expired") remain visible long enough to
 *     read.
 *   - The `?email` query param pre-fills the email/username field — useful
 *     when bouncing the user back from an email-verification link.
 *
 * @param redirectUrl Hard-coded destination URL to send the user to after a
 *   successful (non-2FA) login. Wins over the `?redirect` query param. Use this
 *   when embedding the form inside a flow that knows where the user should end
 *   up regardless of how they arrived.
 */
function LoginForm({redirectUrl = ''}: { redirectUrl?: string }) {
  const {saveToken, user, isLoading} = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const message = sp.get('message');
  const email = sp.get('email');

  useEffect(() => {
    if (!isLoading && user && !message) {
      navigate(routes.dashboard);
    }
  }, [user, isLoading]);

  const initialObject = useMemo(() => ({
    email_or_username: email || '',
    password: '',
  }), [message, email]);

  return (
    <>
      {message && (
        <Alert variant="info">{message}</Alert>
      )}
      <BackendForm<typeof initialObject, { token: string; user: BackendUser; two_factor: false }>
        initialValues={initialObject}
        targetEndpoint={backendEndpoints.login}
        targetMethod="POST"
        onSuccess={({data}) => {
          saveToken(data.token);
          // Hard refresh, such that the login cookie read into the react state;
          if (data.two_factor) {
            window.location.href = routes.twoFactorChallenge;
          } else {
            window.location.href = redirectUrl || sp?.get('redirect') || routes.landing;
          }
        }}
        config={{
          withCredentials: true,
          withXSRFToken: true,
        }}
        footerContent={(
          <div className="py-2 d-inline-flex gap-3">
            <a href={routes.forgotPassword} className="btn-link fw-bold">Forgotten password?</a>
          </div>
        )}
        formId={formId}
      >
        <FloatingLabelField
          name="email_or_username"
          label="E-mail or username"
          icon={<At/>}
          disappearingLabel
          type="text"
        />
        <FloatingLabelField
          type="password"
          name="password"
          label="Password"
          icon={<Lock/>}
          disappearingLabel
        />
        <FormFiller formId={formId} answerSets={answerSets}/>
      </BackendForm>
    </>
  )
}

LoginPage.Form = LoginForm
